import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { Select, Input, InputNumber, Typography, Divider, Space } from 'antd';
import type { DataSourceConfig, DataParserConfig, DataFieldMapping } from '@dtwin/shared';
import { parseData } from '../../services/dataProvider';
import { dataSourceTypeRegistry } from '../../plugins/data-source-type-registry';
import { getStaticExampleFor, getStaticHintFor } from './widgetDataGuides';
import { useDeviceDataStore } from '../../stores/deviceDataStore';
import { ensureDeviceSocket } from '../../services/deviceSocket';
import { fetchIotDevices, type RegisteredDevice } from '../../api/iot';

const { Text } = Typography;

export const FORMAT_OPTIONS = [
  { value: '', label: '原样' },
  { value: 'number', label: '数值' },
  { value: 'string', label: '字符串' },
  { value: 'percent', label: '百分比' },
  { value: 'thousand', label: '千分位' },
  { value: 'date', label: '日期' },
  { value: 'jsonpath', label: '路径取值' },
];

export const DATA_SOURCE_TYPE_OPTIONS = [
  { value: 'static', label: '静态数据' },
  { value: 'api', label: 'REST API' },
  { value: 'websocket', label: 'WebSocket' },
  { value: 'sql', label: 'SQL 查询' },
  { value: 'mqtt', label: 'MQTT 订阅' },
  { value: 'device', label: '设备数据' },
];

/** 合并内置 + 插件数据源类型（阶段3：已启用插件注册的 dataSourceTypes） */
export function getDataSourceTypeOptions() {
  const pluginTypes = dataSourceTypeRegistry.getAll();
  return [
    ...DATA_SOURCE_TYPE_OPTIONS,
    ...pluginTypes.map((t) => ({ value: t.type, label: t.label })),
  ];
}

/** 数据解析编辑器：取值路径 → 字段映射 → 过滤 → JS 转换 + 实时预览 */
export function DataParserEditor({
  parser,
  rawData,
  onChange,
}: {
  parser?: DataParserConfig;
  rawData: unknown;
  onChange: (p: DataParserConfig) => void;
}) {
  const [result, setResult] = useState<unknown | null>(null);

  const update = (patch: Partial<DataParserConfig>) => {
    onChange({ ...(parser || {}), ...patch });
  };

  useEffect(() => {
    try {
      setResult(parseData(rawData, parser));
    } catch {
      setResult(rawData);
    }
  }, [parser, rawData]);

  const rawCount: number | string = Array.isArray(rawData) ? rawData.length : rawData ? '1' : '0';
  const resultCount: number | string = Array.isArray(result) ? result.length : result ? '1' : '0';
  const mapping = parser?.mapping || [];

  const fmt = (v: unknown) => {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  };

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
          数据取值路径 <Text type="secondary" style={{ fontSize: 11 }}>JSONPath 简化: data.list / data[0].value</Text>
        </Text>
        <Input
          placeholder="留空 = 取全部"
          value={parser?.path || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => update({ path: e.target.value })}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>字段映射 / 格式化</Text>
        {mapping.length === 0 && (
          <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 6 }}>未配置字段映射，数据原样输出</Text>
        )}
        {mapping.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <Input
              size="small"
              placeholder="源字段"
              style={{ width: 90 }}
              value={m.sourceField}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = [...mapping];
                next[i] = { ...next[i], sourceField: e.target.value };
                update({ mapping: next });
              }}
            />
            <Text style={{ color: '#555', fontSize: 11 }}>→</Text>
            <Input
              size="small"
              placeholder="目标字段"
              style={{ width: 90 }}
              value={m.targetField || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = [...mapping];
                next[i] = { ...next[i], targetField: e.target.value };
                update({ mapping: next });
              }}
            />
            <Select
              size="small"
              style={{ width: 100 }}
              value={m.type || ''}
              options={FORMAT_OPTIONS}
              onChange={(v) => {
                const next = [...mapping];
                next[i] = { ...next[i], type: v === '' ? undefined : (v as any) };
                update({ mapping: next });
              }}
            />
            {m.type && (
              <Input
                size="small"
                placeholder="参数"
                style={{ width: 100 }}
                value={m.format || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const next = [...mapping];
                  next[i] = { ...next[i], format: e.target.value };
                  update({ mapping: next });
                }}
              />
            )}
            <span
              onClick={() => update({ mapping: mapping.filter((_, j) => j !== i) })}
              style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 13, padding: '0 4px' }}
            >×</span>
          </div>
        ))}
        <span
          style={{ color: '#1677ff', cursor: 'pointer', fontSize: 12 }}
          onClick={() => update({ mapping: [...mapping, { sourceField: '', targetField: '' } as DataFieldMapping] })}
        >+ 添加字段映射</span>
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
          过滤条件 <Text type="secondary" style={{ fontSize: 11 }}>对每条数据求值，true 保留，如 item.value {'>'} 10</Text>
        </Text>
        <Input
          placeholder="留空 = 不过滤"
          value={parser?.filter || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => update({ filter: e.target.value })}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
          JS 转换 <Text type="secondary" style={{ fontSize: 11 }}>对整个数据集执行，入参 data</Text>
        </Text>
        <Input.TextArea
          rows={2}
          placeholder="如 data.slice(0, 10)"
          value={parser?.transform || ''}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => update({ transform: e.target.value })}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
        <Text style={{ fontSize: 11, color: '#888' }}>处理前 {rawCount} 条 → 处理后 {resultCount} 条</Text>
      </div>
      {result !== null && (
        <div style={{
          marginTop: 8, background: '#111320', border: '1px solid #2a2a3e', borderRadius: 4,
          padding: 8, maxHeight: 160, overflow: 'auto', fontSize: 11, color: '#9ecbff', fontFamily: 'monospace', whiteSpace: 'pre',
        }}>
          {fmt(result)}
        </div>
      )}
    </div>
  );
}

/** 数据源配置表单：类型选择 + 各类型配置 + 测试按钮（数据源与数据集页共用） */
export default function DataSourceConfigForm({
  value,
  onChange,
  showTypeSelect = true,
  extra,
  widgetType,
}: {
  value?: DataSourceConfig;
  onChange: (ds: DataSourceConfig) => void;
  showTypeSelect?: boolean;
  extra?: React.ReactNode;
  /** 组件类型（用于静态数据示例引导） */
  widgetType?: string;
}) {
  const [testState, setTestState] = useState<{ status: 'idle' | 'loading' | 'ok' | 'error'; msg: string; raw: unknown }>({ status: 'idle', msg: '', raw: null });

  // 设备注册表：可供选择的设备（按 名称/IMEI 搜索）；含手动输入回退
  const [devices, setDevices] = useState<RegisteredDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [manualDeviceId, setManualDeviceId] = useState(false);

  const ds = value;
  const dsType = ds?.type || 'static';

  // 2026-09-14 修复（FE-22/FE-21）：静态 JSON 输入本地 draft。
  // 原实现 value 由 store 派生 + 仅"整段合法 JSON"才提交 → React 受控回写把中间态字符吞掉（无法粘贴、只能输入数字）；
  // 且非法输入无 lastValid 缓存。改为本地 draft 即时回显，非法输入保留 draft 与上次有效数据（不改 store）。
  const staticDataStr =
    typeof ds?.config === 'object' && ds && 'data' in (ds.config as any)
      ? JSON.stringify((ds.config as any).data, null, 2)
      : '';
  const [staticDraft, setStaticDraft] = useState<string>(staticDataStr);
  const [staticDraftInvalid, setStaticDraftInvalid] = useState(false);
  const lastSyncedRef = useRef<string>(staticDataStr);
  useEffect(() => {
    // 仅外部值变化（切换组件/数据源）时同步 draft；本组件提交触发的规范化回写不覆盖用户正在编辑的文本
    if (staticDataStr !== lastSyncedRef.current) {
      lastSyncedRef.current = staticDataStr;
      setStaticDraft(staticDataStr);
      setStaticDraftInvalid(false);
    }
  }, [staticDataStr]);

  useEffect(() => {
    if (dsType !== 'device') return;
    // 拉取租户可见设备（设备注册表聚合，插件 Provider 提供）
    let cancelled = false;
    setDevicesLoading(true);
    fetchIotDevices().then((list) => {
      if (cancelled) return;
      setDevices(list);
      setDevicesLoading(false);
    });
    return () => { cancelled = true; };
  }, [dsType]);

  // device 数据源：实时点位接入解析预览（数据同源：预览=运行态消费的同一份 deviceDataStore）
  const deviceLive = useDeviceDataStore((s) =>
    ds?.type === 'device' ? s.deviceMap[String((ds as any)?.config?.deviceId ?? '').trim()] : undefined
  );
  useEffect(() => {
    if (ds?.type === 'device') {
      ensureDeviceSocket();
      setTestState({ status: 'ok', msg: '实时设备点位（数据同源）', raw: deviceLive?.points ?? {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ds?.type, (ds as any)?.config?.deviceId, deviceLive]);

  const pluginType = !DATA_SOURCE_TYPE_OPTIONS.some((o) => o.value === dsType)
    ? dataSourceTypeRegistry.get(dsType)
    : undefined;

  // 静态数据示例引导（按组件类型）
  const getStaticPlaceholder = () => getStaticExampleFor(widgetType || '');
  const getStaticHint = () => getStaticHintFor(widgetType || '');

  const runTest = async () => {
    if (!ds) return;
    // device 类型：直接读设备数据中枢（同源预览）
    if (dsType === 'device') {
      const raw = useDeviceDataStore.getState().deviceMap[String((ds.config as any)?.deviceId ?? '').trim()]?.points ?? {};
      setTestState({ status: 'ok', msg: `设备点位 ${Object.keys(raw).length} 个`, raw });
      return;
    }
    setTestState({ status: 'loading', msg: '测试中...', raw: null });
    try {
      // 插件类型：优先用插件的 test/fetch
      if (pluginType) {
        const fn = pluginType.test || pluginType.fetch;
        if (!fn) throw new Error('该插件数据源类型未提供 test/fetch 实现');
        const raw = await fn((ds.config as any) || {});
        setTestState({
          status: 'ok',
          msg: Array.isArray(raw) ? `成功，返回数组 ${raw.length} 条` : '成功，返回对象',
          raw,
        });
      } else {
        const { fetchDataSource } = await import('../../services/dataProvider');
        const raw = await fetchDataSource(ds);
        setTestState({
          status: 'ok',
          msg: Array.isArray(raw) ? `成功，返回数组 ${raw.length} 条` : '成功，返回对象',
          raw,
        });
      }
    } catch (e: any) {
      setTestState({ status: 'error', msg: e?.message || String(e), raw: null });
    }
  };

  const setType = (type: string) => {
    // 2026-09-14 修复（FE-31/测试预期"切回时已填字段仍在"）：切换类型 = 合并（默认键 + 保留已有配置与解析器），不再重建
    setTestState({ status: 'idle', msg: '', raw: null });
    const defaults: Record<string, Record<string, unknown>> = {
      static: { data: '[]' },
      api: { url: '', method: 'GET' },
      websocket: { url: '' },
      sql: { connectionId: '', query: '' },
      mqtt: { brokerUrl: '', topic: '' },
      device: { deviceId: '' },
    };
    onChange({
      ...(ds as DataSourceConfig),
      type,
      config: { ...(defaults[type] || {}), ...((ds?.config as any) || {}) },
    } as DataSourceConfig);
  };

  const updateConfig = (patch: Record<string, unknown>) => {
    onChange({ ...(ds as DataSourceConfig), type: dsType, config: { ...((ds?.config as any) || {}), ...patch } });
  };

  const setParser = (p: DataParserConfig) => {
    onChange({ ...(ds as DataSourceConfig), parser: p });
  };

  return (
    <div>
      {showTypeSelect && (
        <>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8 }}>数据源类型</Text>
          <Select
            role="combobox"
            style={{ width: '100%' }}
            value={dsType}
            onChange={setType}
            options={getDataSourceTypeOptions()}
          />
          <Divider style={{ margin: '12px 0', borderColor: '#333' }} />
        </>
      )}

      {dsType === 'static' && (
        <div>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8 }}>数据 (JSON)</Text>
          <Input.TextArea
            rows={6}
            placeholder={getStaticPlaceholder()}
            value={staticDraft}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              const raw = e.target.value;
              setStaticDraft(raw);
              // 明确清空：提交空数组（"保留上次数据"仅针对非法 JSON 输入）
              if (!raw.trim()) {
                lastSyncedRef.current = JSON.stringify([], null, 2);
                setStaticDraftInvalid(false);
                onChange({ ...(ds as DataSourceConfig), type: 'static', config: { data: [] } });
                return;
              }
              try {
                const data = JSON.parse(raw);
                lastSyncedRef.current = JSON.stringify(data, null, 2);
                setStaticDraftInvalid(false);
                onChange({ ...(ds as DataSourceConfig), type: 'static', config: { data } });
              } catch {
                // 非法 JSON：保留本地编辑内容 + 上次有效数据（不改 store），仅提示
                setStaticDraftInvalid(true);
              }
            }}
          />
          {staticDraftInvalid && (
            <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              JSON 格式无效：已保留上次有效数据
            </Text>
          )}
          <Text style={{ fontSize: 11, color: '#555', display: 'block', marginTop: 4 }}>
            示例（按控件类型自动提示 key）：{getStaticHint()}
          </Text>
        </div>
      )}

      {dsType === 'api' && (
        <Space orientation="vertical" style={{ width: '100%' }} size="small">
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>请求地址</Text>
            <Input
              placeholder="https://api.example.com/data"
              value={(ds?.config as any)?.url || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ url: e.target.value, method: (ds?.config as any)?.method || 'GET' })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>请求方法</Text>
            <Select
              style={{ width: '100%' }}
              value={(ds?.config as any)?.method || 'GET'}
              onChange={(m) => updateConfig({ method: m })}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'DELETE', label: 'DELETE' },
              ]}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>请求头 (JSON)</Text>
            <Input.TextArea
              rows={2}
              placeholder='{"Authorization": "Bearer xxx"}'
              value={
                (ds?.config as any)?.headers && Object.keys((ds?.config as any)?.headers).length
                  ? JSON.stringify((ds?.config as any)?.headers, null, 2)
                  : ''
              }
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                try {
                  const headers = JSON.parse(e.target.value);
                  updateConfig({ headers });
                } catch {}
              }}
            />
          </div>
          {((ds?.config as any)?.method || 'GET') === 'POST' && (
            <div>
              <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>请求体 (JSON)</Text>
              <Input.TextArea
                rows={2}
                placeholder='{"key": "value"}'
                value={(ds?.config as any)?.body || ''}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateConfig({ body: e.target.value })}
              />
            </div>
          )}
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>刷新间隔(秒, 0=不轮询)</Text>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={ds?.refreshInterval}
              onChange={(v: number | null) =>
                onChange({ ...(ds as DataSourceConfig), refreshInterval: v ?? undefined })
              }
            />
          </div>
        </Space>
      )}

      {dsType === 'websocket' && (
        <Space orientation="vertical" style={{ width: '100%' }} size="small">
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
              WebSocket 地址 <Text type="secondary" style={{ fontSize: 11 }}>持续订阅，消息到达即更新</Text>
            </Text>
            <Input
              placeholder="ws://192.168.1.100:8080/ws"
              value={(ds?.config as any)?.url || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ url: e.target.value })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>协议 (可选)</Text>
            <Input
              placeholder="留空"
              value={(ds?.config as any)?.protocol || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ protocol: e.target.value })}
            />
          </div>
        </Space>
      )}

      {dsType === 'sql' && (
        <Space orientation="vertical" style={{ width: '100%' }} size="small">
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
              连接 ID <Text type="secondary" style={{ fontSize: 11 }}>服务端已配置的数据库连接</Text>
            </Text>
            <Input
              placeholder="default"
              value={(ds?.config as any)?.connectionId || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ connectionId: e.target.value })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>SQL 查询</Text>
            <Input.TextArea
              rows={4}
              placeholder="SELECT * FROM table"
              value={(ds?.config as any)?.query || ''}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateConfig({ query: e.target.value })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>刷新间隔(秒)</Text>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={ds?.refreshInterval}
              onChange={(v: number | null) =>
                onChange({ ...(ds as DataSourceConfig), refreshInterval: v ?? undefined })
              }
            />
          </div>
          <Text style={{ fontSize: 11, color: '#faad14', display: 'block' }}>
            注：SQL 数据源需服务端配置受控连接（详见后端 /proxy/query）。
          </Text>
        </Space>
      )}

      {dsType === 'mqtt' && (
        <Space orientation="vertical" style={{ width: '100%' }} size="small">
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Broker URL</Text>
            <Input
              placeholder="mqtt://broker.example.com:1883"
              value={(ds?.config as any)?.brokerUrl || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ brokerUrl: e.target.value })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>订阅主题</Text>
            <Input
              placeholder="device/+/sensor"
              value={(ds?.config as any)?.topic || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ topic: e.target.value })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>刷新间隔(秒)</Text>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={ds?.refreshInterval}
              onChange={(v: number | null) =>
                onChange({ ...(ds as DataSourceConfig), refreshInterval: v ?? undefined })
              }
            />
          </div>
        </Space>
      )}

      {dsType === 'device' && (
        <div>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>设备</Text>
          {!manualDeviceId ? (
            <>
              <Select
                style={{ width: '100%' }}
                placeholder="搜索设备（名称 / IMEI）"
                loading={devicesLoading}
                showSearch
                allowClear
                value={(ds?.config as any)?.deviceId || undefined}
                onChange={(v) => updateConfig({ deviceId: v || '' })}
                filterOption={(input, option) =>
                  [option?.imei, option?.name, option?.value]
                    .filter(Boolean)
                    .some((s) => String(s).toLowerCase().includes(input.toLowerCase()))
                }
                options={devices.map((d) => ({
                  value: d.deviceId,
                  name: d.name,
                  imei: d.imei || d.deviceId,
                  label: (
                    <span>
                      {d.online ? <span style={{ color: '#52c41a', marginRight: 6 }}>●</span> : <span style={{ color: '#888', marginRight: 6 }}>○</span>}
                      {d.name}
                      {d.imei && d.imei !== d.name ? <Text type="secondary" style={{ fontSize: 11 }}> ({d.imei})</Text> : null}
                    </span>
                  ),
                }))}
                notFoundContent={devicesLoading ? '加载中...' : '暂无设备：请先到「插件 → RTU 设备管理」添加/绑定设备，或使用手动输入'}
              />
              <span
                style={{ color: '#1677ff', cursor: 'pointer', fontSize: 11, display: 'inline-block', marginTop: 4 }}
                onClick={() => setManualDeviceId(true)}
              >
                手动输入设备ID
              </span>
            </>
          ) : (
            <>
              <Input
                placeholder="设备ID / IMEI（对应 /ws/iot 推送的 deviceId）"
                value={(ds?.config as any)?.deviceId || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateConfig({ deviceId: e.target.value })}
              />
              <span
                style={{ color: '#1677ff', cursor: 'pointer', fontSize: 11, display: 'inline-block', marginTop: 4 }}
                onClick={() => setManualDeviceId(false)}
              >
                从设备列表选择
              </span>
            </>
          )}
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            选择后实时订阅设备数据中枢：点位作为字段输入（数值/布尔/字符串），可再用下方解析管线加工
          </Text>
        </div>
      )}

      {extra}

      {/* 插件数据源类型：渲染插件自定义 ConfigForm（阶段3） */}
      {pluginType && pluginType.ConfigForm && (
        <pluginType.ConfigForm
          value={(ds?.config as any) || {}}
          onChange={(cfg) => updateConfig(cfg)}
        />
      )}
      {pluginType && !pluginType.ConfigForm && (
        <div style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
            {pluginType.label} 配置 <Text type="secondary" style={{ fontSize: 11 }}>插件未提供配置表单，使用 JSON 配置</Text>
          </Text>
          <Input.TextArea
            rows={4}
            placeholder='{"key": "value"}'
            value={JSON.stringify((ds?.config as any) || {}, null, 2)}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              try { updateConfig(JSON.parse(e.target.value)); } catch {}
            }}
          />
        </div>
      )}

      {dsType !== 'static' && (
        <div style={{ marginTop: 8 }}>
          <span
            onClick={runTest}
            style={{ color: '#1677ff', cursor: 'pointer', fontSize: 12, border: '1px solid #1677ff', padding: '2px 12px', borderRadius: 4, display: 'inline-block' }}
          >
            {testState.status === 'loading' ? '测试中...' : '测试数据源'}
          </span>
          {testState.status === 'ok' && (
            <div style={{
              marginTop: 6, background: '#111320', border: '1px solid #2a2a3e', borderRadius: 4,
              padding: 6, maxHeight: 120, overflow: 'auto', fontSize: 11, color: '#9ecbff', fontFamily: 'monospace', whiteSpace: 'pre',
            }}>
              {(() => { try { return JSON.stringify(testState.raw, null, 2); } catch { return String(testState.raw); } })()}
            </div>
          )}
          {testState.status === 'error' && (
            <div style={{ marginTop: 6, color: '#ff4d4f', fontSize: 11 }}>{testState.msg}</div>
          )}
        </div>
      )}

      <Divider style={{ margin: '14px 0', borderColor: '#333' }} />

      <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>数据解析</Text>
      <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 8 }}>
        按 取值路径 → 字段映射 → 过滤 → JS 转换 顺序处理
      </Text>
      <DataParserEditor
        parser={ds?.parser}
        // 2026-09-14 修复（FE-32）：static 类型不渲染"测试数据源"按钮、testState.raw 恒为 null，
        // 导致解析预览（取值路径/映射/转换）无法验证——static 直接消费当前静态数据实时预览
        rawData={dsType === 'static' ? ((ds?.config as any)?.data ?? null) : testState.raw}
        onChange={setParser}
      />
    </div>
  );
}
